import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Key,
  Shield,
  Search,
  Edit,
  Check,
  X,
  Users,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  userCount?: number;
}

// Recursos disponíveis no sistema
const RESOURCES = [
  { value: 'accounts', label: 'Contas' },
  { value: 'transactions', label: 'Transações' },
  { value: 'users', label: 'Usuários' },
  { value: 'tenants', label: 'Empresas' },
  { value: 'pix', label: 'Pix' },
  { value: 'boleto', label: 'Boletos' },
  { value: 'card', label: 'Cartões' },
  { value: 'split', label: 'Split' },
  { value: 'webhooks', label: 'Webhooks' },
  { value: 'audit', label: 'Auditoria' },
  { value: 'settings', label: 'Configurações' },
];

const ACTIONS = [
  { value: 'create', label: 'Criar' },
  { value: 'read', label: 'Visualizar' },
  { value: 'update', label: 'Editar' },
  { value: 'delete', label: 'Excluir' },
  { value: 'export', label: 'Exportar' },
];

// Roles padrão do sistema
const DEFAULT_ROLES: Role[] = [
  {
    id: 'admin',
    name: 'Administrador',
    description: 'Acesso total ao sistema',
    permissions: ['*'],
    userCount: 0,
  },
  {
    id: 'manager',
    name: 'Gerente',
    description: 'Gerencia contas, transações e usuários',
    permissions: ['accounts:*', 'transactions:*', 'users:read', 'users:update'],
    userCount: 0,
  },
  {
    id: 'operator',
    name: 'Operador',
    description: 'Visualiza e cria transações',
    permissions: ['accounts:read', 'transactions:read', 'transactions:create'],
    userCount: 0,
  },
  {
    id: 'viewer',
    name: 'Visualizador',
    description: 'Apenas visualização',
    permissions: ['accounts:read', 'transactions:read'],
    userCount: 0,
  },
];

export function AdminPermissions() {
  const [activeTab, setActiveTab] = useState<'roles' | 'permissions'>('roles');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });

  // Buscar usuários para contar por role
  const { data: usersData } = useQuery({
    queryKey: ['admin-users-count'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.users.list);
      return response.data as { users: Array<{ type: string }> };
    },
  });

  // Calcular contagem de usuários por tipo
  const userCounts = usersData?.users?.reduce((acc, user) => {
    acc[user.type] = (acc[user.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const roles = DEFAULT_ROLES.map(role => ({
    ...role,
    userCount: role.id === 'admin' ? userCounts['ADMIN'] || 0 :
               role.id === 'manager' ? userCounts['EMPLOYEE'] || 0 :
               role.id === 'operator' ? userCounts['CUSTOMER'] || 0 :
               userCounts['SUPPLIER'] || 0,
  }));

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions,
    });
    setIsRoleModalOpen(true);
  };

  const handleTogglePermission = (permission: string) => {
    setRoleFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleSaveRole = () => {
    // Em produção, isso salvaria no backend
    console.log('Salvando role:', roleFormData);
    setIsRoleModalOpen(false);
    setEditingRole(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Permissões</h1>
          <p className="text-surface-400">Gerencie roles e permissões do sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'roles' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('roles')}
        >
          <Shield className="w-4 h-4 mr-2" />
          Roles
        </Button>
        <Button
          variant={activeTab === 'permissions' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('permissions')}
        >
          <Key className="w-4 h-4 mr-2" />
          Permissões
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-400" />
              Roles do Sistema
              <Badge variant="info">{filteredRoles.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-surface-800">
              {filteredRoles.map(role => (
                <div
                  key={role.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{role.name}</p>
                      <p className="text-sm text-surface-500">{role.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-surface-400">
                      <Users className="w-4 h-4" />
                      {role.userCount} usuários
                    </div>
                    <Badge variant="info">
                      {role.permissions.length === 1 && role.permissions[0] === '*'
                        ? 'Todas'
                        : `${role.permissions.length} permissões`}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleEditRole(role)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary-400" />
              Matriz de Permissões
            </CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-800">
                  <th className="text-left py-3 px-4 text-surface-400 font-medium">Recurso</th>
                  {ACTIONS.map(action => (
                    <th key={action.value} className="text-center py-3 px-4 text-surface-400 font-medium">
                      {action.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RESOURCES.map(resource => (
                  <tr key={resource.value} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                    <td className="py-3 px-4 text-surface-100 font-medium">{resource.label}</td>
                    {ACTIONS.map(action => {
                      const permission = `${resource.value}:${action.value}`;
                      const hasPermission = roles[0].permissions.includes('*') || 
                                           roles[0].permissions.includes(permission) ||
                                           roles[0].permissions.includes(`${resource.value}:*`);
                      return (
                        <td key={action.value} className="text-center py-3 px-4">
                          {hasPermission ? (
                            <Check className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-surface-600 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 p-3 bg-surface-800/50 rounded-lg text-sm text-surface-400">
              <p>Esta matriz mostra as permissões do role Administrador. Selecione um role diferente para ver suas permissões específicas.</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Edit Role Modal */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={() => { setIsRoleModalOpen(false); setEditingRole(null); }}
        title={editingRole ? `Editar Role: ${editingRole.name}` : 'Nova Role'}
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={roleFormData.name}
            onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
            disabled={editingRole?.id === 'admin'}
          />
          <Input
            label="Descrição"
            value={roleFormData.description}
            onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
          />
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Permissões
            </label>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {RESOURCES.map(resource => (
                <div key={resource.value} className="p-3 bg-surface-800/50 rounded-lg">
                  <p className="text-sm font-medium text-surface-200 mb-2">{resource.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {ACTIONS.map(action => {
                      const permission = `${resource.value}:${action.value}`;
                      const isSelected = roleFormData.permissions.includes(permission) ||
                                        roleFormData.permissions.includes('*') ||
                                        roleFormData.permissions.includes(`${resource.value}:*`);
                      return (
                        <button
                          key={action.value}
                          type="button"
                          onClick={() => handleTogglePermission(permission)}
                          disabled={editingRole?.id === 'admin'}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            isSelected
                              ? 'bg-primary-500/30 text-primary-300 border border-primary-500/50'
                              : 'bg-surface-700 text-surface-400 border border-surface-600 hover:border-surface-500'
                          }`}
                        >
                          {action.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {editingRole?.id === 'admin' && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-400">
              O role Administrador não pode ser modificado.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsRoleModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveRole}
              disabled={editingRole?.id === 'admin'}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
